/**
 * React Query hooks for instructor data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  InstructorService, 
  UserActivityFilters,
  GradeOverrideCreate
} from '../services/instructor';

// Query keys
export const instructorQueryKeys = {
  all: ['instructor'] as const,
  overviewStats: (filters?: UserActivityFilters) => [...instructorQueryKeys.all, 'overview-stats', filters] as const,
  userActivity: (filters?: UserActivityFilters) => [...instructorQueryKeys.all, 'user-activity', filters] as const,
  userLabHistory: (userId: number, includeGrades?: boolean, includeFailed?: boolean) =>
    [...instructorQueryKeys.all, 'user-lab-history', userId, { includeGrades, includeFailed }] as const,
  labUsage: () => [...instructorQueryKeys.all, 'lab-usage'] as const,
};

// Hook for overview statistics
export function useOverviewStats(filters?: UserActivityFilters) {
  return useQuery({
    queryKey: instructorQueryKeys.overviewStats(filters),
    queryFn: () => filters ? InstructorService.getOverviewStatsFiltered(filters) : InstructorService.getOverviewStats(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });
}

// Hook for user activity data
export function useUserActivity(filters?: UserActivityFilters) {
  return useQuery({
    queryKey: instructorQueryKeys.userActivity(filters),
    queryFn: () => InstructorService.getUserActivity(filters),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// Hook for individual user lab history
export function useUserLabHistory(
  userId: number,
  includeGrades: boolean = true,
  includeFailed: boolean = false
) {
  return useQuery({
    queryKey: instructorQueryKeys.userLabHistory(userId, includeGrades, includeFailed),
    queryFn: () => InstructorService.getUserLabHistory(userId, includeGrades, includeFailed),
    enabled: !!userId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Hook for lab usage statistics
export function useLabUsage() {
  return useQuery({
    queryKey: instructorQueryKeys.labUsage(),
    queryFn: () => Promise.resolve([]), // Placeholder until getLabUsage is implemented
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Mutation hook for grade overrides
export function useGradeOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ gradeId, data }: { gradeId: number; data: GradeOverrideCreate }) =>
      InstructorService.overrideGrade(gradeId, data),
    onSuccess: (result, variables) => {
      // Invalidate relevant queries to refetch data
      queryClient.invalidateQueries({ queryKey: instructorQueryKeys.overviewStats() });
      queryClient.invalidateQueries({ queryKey: instructorQueryKeys.userActivity() });
      
      // Find the user ID from the grade override result to invalidate specific user history
      // Note: We may need to get userId from the context or pass it as part of the mutation
      queryClient.invalidateQueries({ 
        queryKey: [...instructorQueryKeys.all, 'user-lab-history'],
        exact: false
      });
    },
    onError: (error) => {
      console.error('Failed to override grade:', error);
    },
  });
}

// Hook to invalidate all instructor data (useful for refresh)
export function useRefreshInstructorData() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: instructorQueryKeys.all });
  };
}
